import { SplitFactory } from '@splitsoftware/splitio-browserjs'
import { reactive } from 'vue'
import { logger } from '@/shared/logging/logger'

let factory = null
let client = null

/**
 * Reactive store of feature flag treatments. Components that reference flags
 * from this object will automatically re-render when a flag changes via
 * streaming. Boolean flags are stored as true/false (converted from 'on'/'off').
 *
 * Usage in a component:
 *   import { flags } from '@/shared/featureFlags/featureFlags'
 *   // In template: v-if="flags.frontend_demo_banner_enabled"
 */
export const flags = reactive({})

/**
 * Converts a Split treatment string to a usable value.
 * 'on' -> true, 'off' -> false, 'control' -> false, anything else kept as-is.
 */
function __parseTreatment(treatment) {
  if (treatment === 'on') return true
  if (treatment === 'off' || treatment === 'control') return false
  return treatment
}

/**
 * Initializes the Harness FME (Split) SDK.
 * Should be called once at application startup before mounting the app so
 * initialization begins before components evaluate flags. SDK readiness is
 * asynchronous, so components may still observe default values until ready.
 *
 * @param {string} userKey - Unique identifier for the current user (e.g. user ID or "anonymous")
 * @returns {Promise<void>} Resolves when the SDK is ready and initial flags are loaded
 */
export function initializeFeatureFlags(userKey) {
  const sdkKey = process.env.HARNESS_FME_SDK_KEY

  if (!sdkKey) {
    logger.warn('HARNESS_FME_SDK_KEY is not set. Feature flags will not be evaluated.')
    return Promise.resolve()
  }

  factory = SplitFactory({
    core: {
      authorizationKey: sdkKey,
      key: userKey,
    },
    startup: {
      readyTimeout: 10,
    },
  })

  client = factory.client()

  return new Promise((resolve) => {
    client.on(client.Event.SDK_READY, () => {
      const manager = factory.manager()
      const splitNames = manager.names()
      splitNames.forEach((name) => {
        flags[name] = __parseTreatment(client.getTreatment(name))
      })
      logger.debug('Feature flags initialized.', flags)
      resolve()
    })

    client.on(client.Event.SDK_READY_TIMED_OUT, () => {
      logger.warn('Feature flags SDK timed out during initialization.')
      resolve()
    })

    client.on(client.Event.SDK_UPDATE, () => {
      const manager = factory.manager()
      const splitNames = manager.names()
      splitNames.forEach((name) => {
        flags[name] = __parseTreatment(client.getTreatment(name))
      })
      logger.debug('Feature flags updated.', flags)
    })
  })
}

/**
 * Returns the current value of a feature flag with an optional default.
 * For reactive bindings in templates, prefer importing `flags` directly.
 *
 * @param {string} flagName - The feature flag (split) name
 * @param {*} defaultValue - The value to return if the flag has not been loaded
 * @returns {*} The feature flag value or the default value
 */
export function getFeatureFlag(flagName, defaultValue = false) {
  if (flagName in flags) {
    return flags[flagName]
  }
  if (client) {
    return __parseTreatment(client.getTreatment(flagName))
  }
  return defaultValue
}

/**
 * Closes the feature flags SDK client and cleans up resources.
 * Should be called when the application is torn down.
 */
export function closeFeatureFlags() {
  if (!client) {
    return Promise.resolve()
  }

  return client
    .destroy()
    .catch((err) => {
      logger.warn('Error while destroying feature flags SDK client.', err)
    })
    .finally(() => {
      client = null
      factory = null
    })
}
